import { API, get } from 'utils/api';
import { logError } from './log';
import { isTikTokVideoUrl } from './url';

import type { PostAlike } from 'types';

// will be cleared upon page reload, but it's totally fine
// we just want to avoid spamming TikTok OEmbed API on each rerender which leads to 403 errors
const tempSessionCache: Record<string, { timestamp: number, realUrl: string }> = {};
const oneHourInMS = 60 * 60 * 1000; // cached images will be valid only for one hour

async function getTikTokURLFromBE(videoUrl: string, token?: string): Promise<string | undefined> {
  const endpoint = token ? API.END_POINTS.TIKTOK_POSTS.fetchSharedPostImage(videoUrl) :
    API.END_POINTS.TIKTOK_POSTS.fetchPostImage(videoUrl);
  const config = token ? { headers: { Authorization: token } } : {};

  const response = await get(
    endpoint,
    config,
    {
      suppressGlobalNotification: true,
      suppressAuth: !!token
    });

  return response.data;
}

export async function getTikTokThumbnailUrlFromVideoUrl(videoUrl: string, token?: string): Promise<string> {
  try {
    const tempSessionCacheValue = tempSessionCache[videoUrl];
    if (tempSessionCacheValue && (Date.now() - tempSessionCacheValue.timestamp) < oneHourInMS) {
      return tempSessionCacheValue.realUrl;
    }

    let url;

    try {
      // @see https://developers.tiktok.com/doc/Embed
      const response = (await get(
        `https://www.tiktok.com/oembed?url=${videoUrl}`,
        {},
        {
          suppressAuth: true,
          suppressCollabHeaders: true
        })) as { data: { thumbnailUrl: string } };

      url = response.data.thumbnailUrl;
    } catch (err) {
      url = await getTikTokURLFromBE(videoUrl, token);
    }

    if (url) {
      tempSessionCache[videoUrl] = {
        timestamp: Date.now(),
        realUrl: url
      };
    }

    return url;
  } catch (err) {
    logError(err);

    throw err;
  }
}

export async function getTikTokThumbnailUrl<T extends PostAlike>(
  videoUrl: string,
  size: string,
  post: T
): Promise<string> {
  if (!post.url || !isTikTokVideoUrl(post.url)) {
    return '';
  }

  try {
    return await getTikTokThumbnailUrlFromVideoUrl(videoUrl);
  } catch (err) {
    // try with old post image
    return post.imageUrl ?? '';
  }
}
